import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, Button, Input, Select, Textarea, Field } from "@/components/UI";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PerformerField } from "@/components/VetTable";
import { BirthMonthlyCards } from "./BirthMonthlyCards";
import { today } from "./helpers";


export function Births() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    kind: "birth",
    animal_type: "inek", date: today(), calf_count: 1,
    female_count: 0, male_count: 0, stillborn_count: 0,
    birth_type: "normal",
    abort_count: 1, gestation_week: "", reason: "",
    vet_name: "", notes: "",
  });
  const isAbort = form.kind === "abort";
  // Aylık kartlar için TÜM kayıtları çek (sunucu sayfalaması 500'lük döngüyle birleştirilir)
  const load = async () => {
    let all = [], skip = 0, total = 0;
    try {
      do {
        const r = await api.get(`/vet/births?limit=500&skip=${skip}`);
        all = all.concat(r.data.items || []);
        total = r.data.total || 0; skip += 500;
      } while (all.length < total && skip < 20000);
      setItems(all);
    } catch { /* geçici kesinti — mevcut veriyi koru */ }
  };
  useEffect(() => { load(); }, []);

  const calves = Number(form.calf_count || 0);
  const female = Number(form.female_count || 0);
  const male = Number(form.male_count || 0);
  const stillborn = Number(form.stillborn_count || 0);
  const sum = female + male + stillborn;
  const remaining = calves - sum;
  const overLimit = sum > calves;
  const live = female + male;

  const submit = async (e) => {
    e.preventDefault();
    const vet = form.vet_name?.trim() || user?.name || "";
    try {
      if (isAbort) {
        const ac = Number(form.abort_count || 0);
        if (ac < 1) return toast.error("Atan hayvan adedi en az 1 olmalı");
        await api.post("/vet/births", {
          kind: "abort",
          animal_type: form.animal_type,
          date: form.date,
          abort_count: ac,
          gestation_week: form.gestation_week === "" ? null : Number(form.gestation_week),
          reason: form.reason?.trim() || null,
          vet_name: vet,
          notes: form.notes || null,
        });
        toast.success(`${ac} ${form.animal_type} atık kaydı eklendi`);
        setForm({ ...form, abort_count: 1, gestation_week: "", reason: "", notes: "" });
      } else {
        if (overLimit) return toast.error("Dişi + Erkek + Ölü doğum toplamı doğan yavru sayısını aşamaz");
        await api.post("/vet/births", {
          kind: "birth",
          animal_type: form.animal_type,
          date: form.date,
          calf_count: calves, female_count: female, male_count: male, stillborn_count: stillborn,
          birth_type: form.birth_type,
          vet_name: vet,
          notes: form.notes || null,
        });
        toast.success(`${calves} yavru doğum kaydı eklendi`);
        setForm({ ...form, calf_count: 1, female_count: 0, male_count: 0, stillborn_count: 0, notes: "" });
      }
      load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu doğum kaydını silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/vet/births/${id}`);
      toast.success("Silindi");
      load();
    } catch (err) { toast.error(err.message); }
  };

  const dayMeta = (entries) => {
    const b = entries.filter(x => x.kind !== "abort").reduce((a, x) => a + Number(x.calf_count || 0), 0);
    const a = entries.filter(x => x.kind === "abort").reduce((s, x) => s + Number(x.abort_count || 1), 0);
    return `${b} yavru${a > 0 ? ` · ${a} atık` : ""}`;
  };

  const birthColumns = [
    { header: "Ana", cell: x => <span className="capitalize font-semibold">{x.animal_type || "—"}</span> },
    { header: "Yavru/Adet", thClass: "text-right", tdClass: "text-right tabnum", cell: x => x.kind === "abort"
      ? <span className="text-[#C45B45] font-semibold">{x.abort_count ?? 1}</span>
      : (x.calf_count ?? 1) },
    { header: "Dişi", thClass: "text-right", tdClass: "text-right tabnum text-[#2D4A36] font-semibold", cell: x => x.kind === "abort" ? "—" : (x.female_count ?? 0) },
    { header: "Erkek", thClass: "text-right", tdClass: "text-right tabnum text-[#5A6E5F] font-semibold", cell: x => x.kind === "abort" ? "—" : (x.male_count ?? 0) },
    { header: "Ölü", thClass: "text-right", tdClass: "text-right tabnum text-[#C45B45]", cell: x => x.kind === "abort" ? "—" : (x.stillborn_count ?? 0) },
    { header: "Tip", cell: x => x.kind === "abort" ? (
      <span className="text-xs px-2 py-0.5 rounded bg-[#fbeee9] text-[#C45B45] font-semibold">
        Atık{x.gestation_week != null ? ` · ${x.gestation_week}. hf` : ""}
      </span>
    ) : (
      <span className={`text-xs px-2 py-0.5 rounded ${
        x.birth_type === "normal" ? "bg-[#E4EEE6] text-[#2D4A36]"
        : x.birth_type === "güç" ? "bg-[#fbeee9] text-[#8a3a2b]"
        : "bg-[#F5E9D3] text-[#8B6914]"
      }`}>{x.birth_type}</span>
    ) },
    { header: "Veteriner", cell: x => x.vet_name || "—" },
    { header: "Notlar", tdClass: "text-[#5e675e] text-sm max-w-[220px] whitespace-pre-wrap break-words", cell: x => (
      <div>
        {x.kind === "abort" && x.reason && <div className="text-[#8a3a2b] font-medium">Sebep: {x.reason}</div>}
        <div>{x.notes || (x.kind === "abort" && x.reason ? "" : "—")}</div>
      </div>
    ) },
    { header: "", thClass: "text-right", tdClass: "text-right", cell: x => (
      <button onClick={() => remove(x.id)} data-testid={`birth-del-${x.id}`} title="Kaydı sil" className="text-[#C45B45] hover:text-[#8a3a2b]"><Trash size={16} /></button>
    ) },
  ];

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Card>
        <h3 className="heading text-lg mb-3">{isAbort ? "Atık (Abort) Kaydı" : "Doğum Kaydı"}</h3>
        <form onSubmit={submit} className="space-y-3">
          <div className="flex gap-2" data-testid="birth-kind-toggle">
            <button type="button" data-testid="birth-kind-birth" onClick={() => setForm({ ...form, kind: "birth" })}
              className={`flex-1 px-3 py-2 rounded-md text-sm font-semibold border ${!isAbort ? "bg-[#2D4A36] text-white border-[#2D4A36]" : "bg-white text-[#5e675e] border-[#E5E2DC] hover:border-[#2D4A36]"}`}>
              Doğum
            </button>
            <button type="button" data-testid="birth-kind-abort" onClick={() => setForm({ ...form, kind: "abort" })}
              className={`flex-1 px-3 py-2 rounded-md text-sm font-semibold border ${isAbort ? "bg-[#C45B45] text-white border-[#C45B45]" : "bg-white text-[#5e675e] border-[#E5E2DC] hover:border-[#C45B45]"}`}>
              Atık
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ana Tipi *">
              <Select data-testid="birth-animal-type" value={form.animal_type} onChange={e => setForm({ ...form, animal_type: e.target.value })}>
                <option value="inek">İnek</option>
                <option value="düve">Düve</option>
              </Select>
            </Field>
            {isAbort ? (
              <Field label="Atan Hayvan Adedi *">
                <Input data-testid="abort-count" type="number" min="1" max="50" required
                  value={form.abort_count} onChange={e => setForm({ ...form, abort_count: e.target.value })} />
              </Field>
            ) : (
              <Field label="Doğan Yavru *">
                <Input data-testid="birth-calf-count" type="number" min="1" max="10" required
                  value={form.calf_count} onChange={e => setForm({ ...form, calf_count: e.target.value })} />
              </Field>
            )}
          </div>

          {!isAbort && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Canlı Dişi">
                  <Input data-testid="birth-female" type="number" min="0" value={form.female_count} onChange={e => setForm({ ...form, female_count: e.target.value })} />
                </Field>
                <Field label="Canlı Erkek">
                  <Input data-testid="birth-male" type="number" min="0" value={form.male_count} onChange={e => setForm({ ...form, male_count: e.target.value })} />
                </Field>
                <Field label="Ölü Doğan">
                  <Input data-testid="birth-stillborn" type="number" min="0" value={form.stillborn_count} onChange={e => setForm({ ...form, stillborn_count: e.target.value })} />
                </Field>
              </div>

              {calves > 0 && (
                <div className={`text-xs px-3 py-2 rounded-md border ${overLimit ? "bg-[#fbeee9] border-[#f2d4cb] text-[#8a3a2b]" : "bg-[#E4EEE6] border-[#c2d6c8] text-[#2D4A36]"}`} data-testid="birth-preview">
                  {overLimit ? (
                    <>⚠️ Toplam ({sum}) doğan yavru sayısını ({calves}) aşıyor</>
                  ) : (
                    <>✓ {calves} yavru için: <b>{female}</b> dişi, <b>{male}</b> erkek, <b>{stillborn}</b> ölü doğum
                    {remaining > 0 && <span className="text-[#5e675e]"> · {remaining} sınıflandırılmamış</span>}
                    {live > 0 && <div className="mt-0.5">Dişi oranı: <b>%{((female / live) * 100).toFixed(0)}</b> · Erkek oranı: <b>%{((male / live) * 100).toFixed(0)}</b></div>}
                    </>
                  )}
                </div>
              )}

              <Field label="Doğum Tipi *">
                <Select data-testid="birth-type" value={form.birth_type} onChange={e => setForm({ ...form, birth_type: e.target.value })}>
                  <option value="normal">Normal</option>
                  <option value="güç">Güç doğum</option>
                  <option value="sezaryen">Sezaryen</option>
                </Select>
              </Field>
            </>
          )}

          {isAbort && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Gebelik Haftası (ops.)">
                <Input data-testid="abort-gestation" type="number" min="0" max="45" placeholder="ör. 5"
                  value={form.gestation_week} onChange={e => setForm({ ...form, gestation_week: e.target.value })} />
              </Field>
              <Field label="Sebep (opsiyonel)">
                <Input data-testid="abort-reason" placeholder="ör. enfeksiyon, travma…"
                  value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} />
              </Field>
            </div>
          )}

          <Field label="Tarih *"><Input type="date" data-testid="birth-date" required value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></Field>
          <PerformerField label="Veteriner (İşlemi Yapan)" user={user} value={form.vet_name}
            onChange={v => setForm({ ...form, vet_name: v })} testId="birth-vet" />
          <Field label="Notlar"><Textarea data-testid="birth-notes" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field>
          <Button data-testid="birth-submit" type="submit" disabled={!isAbort && overLimit}><Plus size={14} className="inline mr-1" /> Kaydet</Button>
        </form>
      </Card>

      <BirthMonthlyCards items={items} columns={birthColumns} dayMeta={dayMeta} />
    </div>
  );
}


